import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  { q: "What ages does Jonna work with?", a: "Jonna specializes in students ages 5–13 (Kindergarten through 8th grade), with deep experience in elementary and middle-school reading intervention." },
  { q: "What is Orton-Gillingham and why does it work?", a: "Orton-Gillingham is a structured, multi-sensory approach to teaching reading proven by decades of research to help kids with dyslexia. It teaches the predictable patterns of English explicitly — sight, sound, and touch together — so the brain builds reliable reading pathways." },
  { q: "How do I know if my child has dyslexia?", a: "Common signs include difficulty sounding out words, mixing up similar letters, slow or labored reading, avoiding reading aloud, trouble with spelling, and reading well below grade-level expectations. An assessment is the surest way — Jonna can screen for signs and refer for full evaluation if needed." },
  { q: "How often should we tutor?", a: "Frequency depends on each child's individual needs. However, 1×/week is recommended for kids supporting current learning or preventing the summer slide, and 2×/week for students who need to close significant gaps." },
  { q: "Do you offer virtual tutoring?", a: "Yes. Jonna delivers the same evidence-based 1:1 instruction live over video, using interactive digital letter tiles and multi-sensory tools. Available to families anywhere in the U.S." },
  { q: "What does a typical session look like?", a: "Each 60-minute session is custom-built from your child's assessment results. It includes phonemic awareness warm-ups, explicit phonics instruction, decoding practice with real text, fluency work, spelling and written instruction, and a check-for-understanding. Sessions are firm but fun — kids leave feeling capable." },
  { q: "Where are you located?", a: "Jonna serves Springboro, OH and surrounding areas in-person, and offers virtual tutoring to families nationwide." },
  { q: "Do you accept insurance?", a: "Reading tutoring is generally not insurance-eligible. Jonna does provide written progress reports that can be useful for IEP/504 meetings and as documentation for educational decisions." },
  { q: "How long until we see results?", a: "Most families see meaningful improvement in confidence and decoding accuracy within 8–12 weeks of consistent sessions. Significant gap-closing typically takes a year or more of consistent work — dyslexia doesn't go away, but kids absolutely learn to read." },
  { q: "Can you work with our child's school or IEP team?", a: "Yes. With your written permission, Jonna can coordinate with classroom teachers, intervention specialists, and IEP teams to align goals and recommendations." },
  {
    q: "Where can I learn more about dyslexia?",
    a: (
      <>
        A few trusted resources Jonna recommends for families:
        <ul className="mt-3 space-y-2 list-disc list-inside">
          <li>
            <a href="https://dyslexiaida.org" target="_blank" rel="noopener noreferrer" className="text-terracotta link-underline font-medium">International Dyslexia Association</a>
            <span className="text-ink/60"> — dyslexiaida.org</span>
          </li>
          <li>
            <a href="https://dyslexia.yale.edu/" target="_blank" rel="noopener noreferrer" className="text-terracotta link-underline font-medium">Yale Center for Dyslexia &amp; Creativity</a>
            <span className="text-ink/60"> — dyslexia.yale.edu</span>
          </li>
          <li>
            <a href="https://www.understood.org" target="_blank" rel="noopener noreferrer" className="text-terracotta link-underline font-medium">Understood</a>
            <span className="text-ink/60"> — understood.org</span>
          </li>
        </ul>
      </>
    ),
  },
];

export default function FAQ() {
  return (
    <main className="bg-cream">
      <section className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="faq-hero">
        <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">FAQ</span>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
          Common questions from parents.
        </h1>
        <p className="mt-6 text-lg text-ink/70 leading-relaxed">
          Don't see your question? <a href="/contact" className="text-terracotta link-underline font-medium">Reach out</a> — Jonna reads every message.
        </p>

        <Accordion type="single" collapsible className="mt-12 w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-ink/10">
              <AccordionTrigger className="text-left font-display text-lg font-medium text-ink hover:no-underline py-5" data-testid={`faq-page-q-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink/70 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  );
}
